import { Helmet } from 'react-helmet-async'
import { useLocale, LOCALES, LOCALE_HTML_LANG, LOCALE_OG, Locale, prefixLocale } from '@/i18n'

const SITE_URL = 'https://faq.rexhook.com'
const SITE_NAME = 'RexHook'
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`
const TWITTER_HANDLE = '@REXHOOKS'

interface SEOProps {
  title: string
  description: string
  section?: string
  sectionTitle?: string
  slug?: string
  type?: 'website' | 'article'
  keywords?: string[]
  noindex?: boolean
}

function buildAbsoluteUrl(localizedPath: string) {
  return `${SITE_URL}${localizedPath === '/' ? '' : localizedPath}`
}

export function SEO({
  title,
  description,
  section,
  sectionTitle,
  slug,
  type = 'article',
  keywords = [],
  noindex = false
}: SEOProps) {
  const { locale, t } = useLocale()
  const fullTitle = `${title} | ${t('seo.faq.titleSuffix')}`
  const basePath = section && slug ? `/faq/${section}/${slug}` : '/'
  const localizedPath = prefixLocale(basePath, locale)
  const url = buildAbsoluteUrl(localizedPath)

  // Default keywords for all pages
  const defaultKeywords = [
    'RexHook',
    'Uniswap V4',
    'V4 hooks',
    'DeFi',
    'token launch',
    'hook marketplace',
    'crypto infrastructure',
    'MEV protection',
    'dynamic fees'
  ]
  const allKeywords = [...new Set([...defaultKeywords, ...keywords])]

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: LOCALE_HTML_LANG[locale],
    mainEntity: [
      {
        '@type': 'Question',
        name: title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: description
        }
      }
    ]
  }

  const breadcrumbItems: any[] = [
    {
      '@type': 'ListItem',
      position: 1,
      name: t('doc.home'),
      item: buildAbsoluteUrl(prefixLocale('/', locale)) || SITE_URL
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'FAQ',
      item: buildAbsoluteUrl(prefixLocale('/faq', locale))
    }
  ]

  if (section && sectionTitle) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: sectionTitle,
      item: buildAbsoluteUrl(prefixLocale(`/faq/${section}`, locale))
    })
  }

  if (slug && title) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: breadcrumbItems.length + 1,
      name: title,
      item: url
    })
  }

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems
  }

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    inLanguage: LOCALE_HTML_LANG[locale],
    headline: title,
    description: description,
    url: url,
    datePublished: '2025-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo.svg`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    }
  }

  return (
    <Helmet htmlAttributes={{ lang: LOCALE_HTML_LANG[locale] }}>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords.join(', ')} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Author and publisher */}
      <meta name="author" content={SITE_NAME} />
      <link rel="author" href={SITE_URL} />

      {/* Open Graph tags for social sharing */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} - ${title}`} />
      <meta property="og:locale" content={LOCALE_OG[locale]} />
      {LOCALES.filter(l => l !== locale).map(l => (
        <meta key={l} property="og:locale:alternate" content={LOCALE_OG[l as Locale]} />
      ))}

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} - ${title}`} />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Hreflang */}
      {LOCALES.map(l => (
        <link
          key={l}
          rel="alternate"
          hrefLang={LOCALE_HTML_LANG[l as Locale]}
          href={buildAbsoluteUrl(prefixLocale(basePath, l as Locale))}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={buildAbsoluteUrl(prefixLocale(basePath, 'en'))} />

      {/* Additional SEO tags */}
      <meta name="application-name" content={SITE_NAME} />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      <meta name="theme-color" content="#060608" />

      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(faqStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(articleStructuredData)}
      </script>
    </Helmet>
  )
}

// Homepage-specific SEO with Organization schema
interface HomeSEOProps {
  title?: string
  description?: string
}

export function HomeSEO({ title, description }: HomeSEOProps) {
  const { locale, t } = useLocale()
  const resolvedTitle = title || t('seo.home.title')
  const resolvedDescription = description || t('seo.home.description')
  const fullTitle = `${resolvedTitle} | ${t('seo.home.titleSuffix')}`
  const localizedPath = prefixLocale('/', locale)
  const url = buildAbsoluteUrl(localizedPath) || SITE_URL

  const keywords = [
    'RexHook',
    'Uniswap V4',
    'V4 hooks',
    'DeFi infrastructure',
    'token launch platform',
    'hook marketplace',
    'crypto',
    'blockchain',
    'MEV protection',
    'dynamic fees',
    'anti-sniper',
    'smart contracts',
    'Ethereum',
    'Base',
    'Arbitrum',
    'DeFi',
    'decentralized finance'
  ]

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'RexHook',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.svg`,
    description: 'The open infrastructure layer for token launches on Uniswap V4',
    sameAs: [
      'https://x.com/REXHOOKS',
      'https://t.me/rexhook',
      'https://discord.gg/rexhook'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@rexhook.com',
      contactType: 'customer support'
    }
  }

  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    inLanguage: LOCALE_HTML_LANG[locale],
    name: 'RexHook FAQ',
    url: SITE_URL,
    description: resolvedDescription,
    publisher: {
      '@type': 'Organization',
      name: 'RexHook'
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  }

  return (
    <Helmet htmlAttributes={{ lang: LOCALE_HTML_LANG[locale] }}>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="keywords" content={keywords.join(', ')} />

      {/* Author and publisher */}
      <meta name="author" content="RexHook" />
      <link rel="author" href={SITE_URL} />

      {/* Open Graph tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="RexHook" />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="RexHook" />
      <meta property="og:locale" content={LOCALE_OG[locale]} />
      {LOCALES.filter(l => l !== locale).map(l => (
        <meta key={l} property="og:locale:alternate" content={LOCALE_OG[l as Locale]} />
      ))}

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
      <meta name="twitter:image:alt" content="RexHook" />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Hreflang */}
      {LOCALES.map(l => (
        <link
          key={l}
          rel="alternate"
          hrefLang={LOCALE_HTML_LANG[l as Locale]}
          href={buildAbsoluteUrl(prefixLocale('/', l as Locale)) || SITE_URL}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={SITE_URL} />

      {/* Additional SEO tags */}
      <meta name="application-name" content="RexHook" />
      <meta name="apple-mobile-web-app-title" content="RexHook" />
      <meta name="theme-color" content="#060608" />

      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteStructuredData)}
      </script>
    </Helmet>
  )
}
